// frontend/src/hooks/useMapController.js

import { useEffect, useRef, useCallback } from 'react';
import logger from '../utils/logger';

const coordinatesEqual = (first, second) => {
    if (!Array.isArray(first) || !Array.isArray(second)) return false;
    if (first.length !== 2 || second.length !== 2) return false;
    return Number(first[0]) === Number(second[0]) &&
        Number(first[1]) === Number(second[1]);
};

export const useMapController = ({
    selectedRoute,
    currentLocation,
    map,
    defaultCenter,
    onCenterEntity,
    onCenterReset,
    isInitialLoad,
    isInitialLoadRef
}) => {
    const previousRouteRef = useRef(null);
    const previousDefaultCenterRef = useRef(null);
    const isManualCenterRef = useRef(false);

    const getIsInitialLoad = useCallback(() => {
        if (isInitialLoadRef && typeof isInitialLoadRef === 'object') {
            return Boolean(isInitialLoadRef.current);
        }

        return Boolean(isInitialLoad);
    }, [isInitialLoad, isInitialLoadRef]);
    
    // Get valid waypoints
    const getValidWaypoints = useCallback((route) => {
        if (!route?.waypoints?.length) return [];
        return route.waypoints
            .filter(wp => wp.latitude && wp.longitude)
            .map(wp => [Number(wp.latitude), Number(wp.longitude)]);
    }, []);
    
    // Function to explicitly center the map (call this from button)
    const centerMap = useCallback(() => {
        if (!map) return;
        
        // Signal to parent that we're manually centering
        if (onCenterReset) {
            onCenterReset();
        }

        logger.debug('Manually centering map');
        isManualCenterRef.current = true;
        
        try {
            if (selectedRoute?.waypoints?.length > 0) {
                // Center on route if available
                const waypoints = getValidWaypoints(selectedRoute);
                if (waypoints.length > 0) {
                    // For manual centering or initial load, we want to center on the route
                    logger.debug('Centering on route waypoints');
                    map.fitBounds(waypoints, {
                        padding: [50, 50],
                        animate: true,
                        duration: 0.5
                    });
                    
                    if (onCenterEntity) {
                        onCenterEntity(false); // Hide default marker
                    }
                    return;
                }
            }
            
            // Fallback options if no route or route centering failed
            if (currentLocation?.lat && currentLocation?.lng) {
                logger.debug('Centering on current location');
                map.setView([currentLocation.lat, currentLocation.lng], 13, {
                    animate: true,
                    duration: 0.5
                });
                
                if (onCenterEntity) {
                    onCenterEntity(false);
                }
            } else if (defaultCenter && Array.isArray(defaultCenter) && defaultCenter.length === 2) {
                logger.debug('Centering on entity coordinates');
                map.setView(defaultCenter, 13, {
                    animate: true,
                    duration: 0.5
                });
                
                if (onCenterEntity) {
                    onCenterEntity(true);
                }
            }
        } catch (e) {
            logger.error('Error in centerMap:', e);
        }
    }, [map, selectedRoute, currentLocation, defaultCenter, getValidWaypoints, onCenterEntity, onCenterReset]);
    
    // Maintain current view - a simpler function that just keeps the map where it is
    const maintainCurrentView = useCallback(() => {
        if (!map) return;
        
        try {
            // Simply set the view to the current center and zoom
            // This effectively prevents any motion while still allowing re-renders
            const currentCenter = map.getCenter();
            const currentZoom = map.getZoom();
            
            logger.debug('Maintaining current map view', {
                hasCurrentCenter: Boolean(currentCenter),
                currentZoom
            });
            
            map.setView([currentCenter.lat, currentCenter.lng], currentZoom, {
                animate: false,
                duration: 0
            });
        } catch (e) {
            // If there's an error getting center/zoom, it means the map isn't ready yet
            logger.warn('Map not ready for maintainCurrentView:', e.message);
            
            // Try to use the centerMap function instead, which is more robust
            if (getIsInitialLoad() || isManualCenterRef.current || selectedRoute?.id) {
                logger.debug('Falling back to centerMap instead');
                centerMap();
            }
        }
    }, [map, centerMap, getIsInitialLoad, selectedRoute?.id]);

    // Handle initial load, route changes, or when manual centering is requested
    useEffect(() => {
        if (!map) return;
        
        try {
            const routeId = selectedRoute?.id ?? null;

            // View reset boundaries are owned by this hook instance. Shared
            // module state caused live tracking and route-management maps to
            // inherit each other's last route or entity center.
            const isNewRoute = routeId !== null && routeId !== previousRouteRef.current;
            const isNewDefaultCenter = Array.isArray(defaultCenter) &&
                defaultCenter.length === 2 &&
                !coordinatesEqual(defaultCenter, previousDefaultCenterRef.current);
            const currentIsInitialLoad = getIsInitialLoad();

            logger.debug('Map controller viewport decision', {
                isInitialLoad: currentIsInitialLoad,
                routeId,
                previousRouteId: previousRouteRef.current,
                isNewRoute,
                isNewDefaultCenter,
                hadPreviousDefaultCenter: Boolean(previousDefaultCenterRef.current),
                isManualCenter: isManualCenterRef.current
            });
            
            if (currentIsInitialLoad || isNewRoute || isNewDefaultCenter || isManualCenterRef.current) {
                logger.debug(
                    currentIsInitialLoad ? 'Initial load' :
                    isNewRoute ? 'New route selected' :
                    isNewDefaultCenter ? 'New default center' :
                    'Manual centering requested'
                );
                
                // For these legitimate cases, actually center the map
                centerMap();
                
                // Reset the flags
                if (Array.isArray(defaultCenter) && defaultCenter.length === 2) {
                    previousDefaultCenterRef.current = [...defaultCenter];
                }
                logger.debug('Updated map controller viewport refs', {
                    routeId,
                    hasDefaultCenter: Array.isArray(previousDefaultCenterRef.current)
                });
                isManualCenterRef.current = false;
            } else {
                // For all other cases (tooltip changes, GPS updates, etc.),
                // just maintain the current view
                maintainCurrentView();
            }

            previousRouteRef.current = routeId;
        } catch (e) {
            logger.error('Error in map controller effect:', e);
            
            // If there's an error in the effect, try to center the map as a fallback
            if (map && (getIsInitialLoad() || selectedRoute?.id)) {
                try {
                    centerMap();
                } catch (innerError) {
                    logger.error('Fallback centering also failed:', innerError);
                }
            }
        }
    }, [map, selectedRoute?.id, defaultCenter, centerMap, maintainCurrentView, getIsInitialLoad]);
    
    return { 
        centerMap,
        maintainCurrentView 
    };
};
